import React from "react";
import "./Notification.css";
import { Avatar } from "@mui/material";
import { useApp } from "./../../AppProvider";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import UTCDateTime from "../UTCDateTime";
import api from "./../../axiosInstance"
import Massage from "../MassagePopUp/Massage";
import { getPatientAge } from "../../Utils";
import logger from "../../utils/logger";
// ================= MOCK DATA =================
const users = [];

// ================= HELPERS =================
function getEventColor(eventType) {
  switch (eventType) {
    case "New": return "new-bg";
    case "Regular": return "regular-bg";
    case "Critical": return "critical-bg";
    case "Published": return "published-bg";
    default: return "";
  }
}

function getTextColor(eventType) {
  switch (eventType) {
    case "New": return "new-tx";
    case "Regular": return "regular-tx";
    case "Critical": return "critical-tx";
    case "Published": return "published-tx";
    default: return "";
  }
}

// ================= COMPONENT =================
const Notification = () => {
  const { stringAvatar, stringToColor } = useApp();
  const [selectedDate, setSelectedDate] = React.useState(null);
  const [selectedNotificationCard, setSelectedNotificationCard] = React.useState(null);
  const [pendingAppointments, setPendingAppointments] = React.useState([]);
  const [selectedPatient, setSelectedPatient] = React.useState(null);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');


  // -------- Fetch pending appointments --------
  const fetchPendingAppointments = async () => {
    try {
      const response = await api.get('/appointments/pending');

      setPendingAppointments(response.data);
      return response.data;
    } catch (error) {
      logger.error("Error fetching pending appointments:", error.message);
      return [];
    }
  };
  const fetchPatientObj = async (id) => {
    try {
      const response = await api.get(`/patients/${id}`);

      // se(response.data);
      return response.data;
    } catch (error) {
      logger.error("Error fetching pending appointments:", error.message);
      return [];
    }
  };

  React.useEffect(() => {
    fetchPendingAppointments();
  }, []);

  React.useEffect(() => {
    const loadPatient = async () => {
      if (selectedNotificationCard?.patientId) {
        try {
          const data = await fetchPatientObj(
            selectedNotificationCard.patientId
          );
          setSelectedPatient(data[0]);
        } catch (error) {
          logger.error("Error fetching patient:", error.message);
        }
      } else {
        setSelectedPatient(null);
      }
    };

    loadPatient();
  }, [selectedNotificationCard]);


  // -------- Handle users change --------
  React.useEffect(() => {
    if (pendingAppointments.length > 0) {
      setSelectedNotificationCard(pendingAppointments[0]);
    } else {
      setSelectedNotificationCard(null);
    }
  }, [pendingAppointments]);

  // -------- Filter users --------
  const filteredUsers = pendingAppointments
    // 1️⃣ Filter by selected date
    .filter((item) => {
      if (!selectedDate) return true;

      const appointmentDate = new Date(item.dateOfAppointment);
      const selected = new Date(selectedDate);

      return (
        appointmentDate.getFullYear() === selected.getFullYear() &&
        appointmentDate.getMonth() === selected.getMonth() &&
        appointmentDate.getDate() === selected.getDate()
      );
    })
    // 2️⃣ Sort A → B (earliest to latest)
    .sort((a, b) => {
      return (
        new Date(a.dateOfAppointment).getTime() -
        new Date(b.dateOfAppointment).getTime()
      );
    });


  const handleAccept = async (appointment) => {
    try {
      if (!appointment?._id) return;
      await api.put(`/appointments/accept/${appointment._id}`, {})
      setSuccessMessage("Appointments is accepted. ✅");
      await fetchPendingAppointments();
    } catch (error) {
      logger.error("Error accepting appointment:", error.message);
      setErrorMessage("Failed to accept appointment. Please try again.❌");
    }
  };

  const handleDecline = async (appointment) => {
    try {
      if (!appointment?._id) return;
      await api.delete(`/appointments/cancel/${appointment._id}`)

      setSuccessMessage("Appointments is declined. ❌");
      // ✅ Update UI immediately (optimistic update)
      await fetchPendingAppointments();

    } catch (error) {
      logger.error("Error declining appointment:", error.message);
      setErrorMessage("Failed to accept appointment. Please try again.❌");
    }
  }


  const activePatientDetails = selectedNotificationCard;

  const getPatientName = (patient) => {
    if (!patient) return "";

    // Case 1: already a string
    if (patient.patientName) {
      return patient.patientName;
    }

    // Case 2: object name
    if (patient.name?.firstName || patient.name?.lastName) {
      return `${patient.name?.firstName ?? ""} ${patient.name?.lastName ?? ""}`.trim();
    }

    return "";
  };
  const fullName = getPatientName(activePatientDetails);
  const hasImage = Boolean(activePatientDetails?.img);

  // ================= RENDER =================
  return (
    <>
      {errorMessage && (<Massage message={errorMessage} clearError={() => setErrorMessage('')} type="error" />)}
      {successMessage && (<Massage message={successMessage} clearError={() => setSuccessMessage('')} type="success" />)}
      <div className="notification__main-container">
        {/* ================= SIDEBAR ================= */}
        <div className="sidebar__container">
          {selectedPatient ? (
            <>
              <div className="sidebar__header">
                <Avatar
                  sx={{ width: { md: 80, lg: 90, xl: 120 }, height: { md: 80, lg: 90, xl: 120 }, marginBottom: 1, bgcolor: hasImage ? "transparent" : stringToColor(fullName) }}
                  alt={fullName || null}
                  src={hasImage ? selectedPatient.img : undefined}
                >
                  {!hasImage && stringAvatar(fullName).children}
                </Avatar>

                <h3 className="sidebar__header-name">{fullName}</h3>
              </div>

              <div className="heading__headlines">General Info</div>
              <table className="notification__pataients-info">
                <tbody>
                  <tr><td>Gender</td><td>{selectedPatient.gender || "NA"}</td></tr>
                  <tr><td>Age</td><td>{getPatientAge(selectedPatient)}</td></tr>
                  <tr><td>Weight</td><td>{selectedPatient.weight || "NA"}</td></tr>
                  <tr><td>Blood Group</td><td>{selectedPatient.bloodGroup || "NA"}</td></tr>
                  <tr><td>Phone Number</td><td>{selectedPatient.contact?.phoneNumber?.phoneNumber || "NA"}</td></tr>
                </tbody>
              </table>
              <div className="heading__headlines">Symptoms</div>
              <div className="symtom__box-noti-container">
                {activePatientDetails?.symptoms?.length ? (
                  activePatientDetails.symptoms.map((item, i) => (
                    <div key={i} className="symtom__box-noti">{item.name}</div>
                  ))
                ) : (
                  <p className="no-data-text">No symptoms</p>
                )}
              </div>

              <div className="heading__headlines">Diseases</div>
              <div className="symtom__box-noti-container">
                {activePatientDetails?.diseases?.length ? (
                  activePatientDetails.diseases.map((item, i) => (
                    <div key={i} className="symtom__box-noti">{item.name}</div>
                  ))
                ) : (
                  <p className="no-data-text">No diseases</p>
                )}
              </div>
              <div className="heading__headlines">Note</div>
              <div className="noti__notes-box">
                {activePatientDetails?.thingsToRemember || "No additional notes."}
              </div>
            </>
          ) : (
            <p className="no-data-text-sidebar">Choose a patient to see their appointment details.</p>
          )}
        </div>

        {/* ================= FEED ================= */}
        <div className="feed__container">
          <div className="notification__header">
            <h4>Patients Booked Appointments Notification</h4>
            <div className="notification-filter__calendar">
              <DatePicker
                selected={selectedDate}
                onChange={setSelectedDate}
                isClearable
                placeholderText="Select date"
                className="date--picker"
              />
              <CalendarMonthIcon />
            </div>
          </div>

          {filteredUsers.length === 0 ? (
            <div className="no-data-container">
              <p className="no-data-text">You're all caught up! No new appointment notifications.</p>
            </div>
          ) : (
            <div className="notification__card-container-main">
              {filteredUsers.map((item) => {
                const fullName = item.patientName;
                const isSelected = selectedNotificationCard?._id === item._id;

                return (
                  <div
                    key={item._id}
                    className="notification__card"
                    style={{ backgroundColor: isSelected ? "#D5F3F8" : "#FFF" }}
                    onClick={() => setSelectedNotificationCard(item)}
                  >
                    <div className="card__left-box">
                      <Avatar
                        sx={{ width: 40, height: 40, marginBottom: 1, bgcolor: hasImage ? "transparent" : stringToColor(fullName) }}
                        alt={fullName || null}
                        src={hasImage ? activePatientDetails.img : undefined}
                      >
                        {!hasImage && stringAvatar(fullName).children}
                      </Avatar>
                      <p>{fullName}</p>
                    </div>
                    <div className="time__box">
                      <AccessTimeIcon className="icon__noti" />
                      <UTCDateTime date={item.dateOfAppointment} />
                    </div>
                    <div className="time__box">
                      <InsertInvitationIcon className="icon__noti" />
                      <UTCDateTime date={item.dateOfAppointment} showDate />
                    </div>
                    <span className={`tag-text-box ${getEventColor(item.tags?.[0])} ${getTextColor(item.tags?.[0])}`}>
                      {item.tags?.[0] || "New"}
                    </span>
                    <div className="button__container">
                      <span className="accept__button"
                        onClick={(e) => {
                          e.stopPropagation(); // prevents card click
                          handleAccept(item);
                        }} >Accept</span>
                      <span className="decline__button" onClick={(e) => {
                        e.stopPropagation(); // prevents card click
                        handleDecline(item);
                      }}>Decline</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Notification;
